'use client';
import dynamic from 'next/dynamic';
import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import DynamicComponent from '@repo/ui/src/components/DynamicComponent';
import { useIsMobile, useReducedMotion } from '@repo/ui/src/utils/useMediaQuery';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import useWindowSize from '@repo/ui/src/hooks/useWindowSize';
import { HorizontalAlignmentEnum, VerticalAlignmentEnum } from '@repo/ui/src/enums/shared-enums';
import { imageFullWidthXs, imageFullWidthSmAndUp } from '@repo/ui/src/styles/variables';
import { ImageProps, MediaType, ToutContentProps } from '@repo/ui/src/types';

const BackgroundVideo = dynamic(() => import('@repo/ui/src/components/BackgroundVideo'));

const VIDEO_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/video-link';

interface CalloutCardProps {
  backgroundImage?: ImageProps;
  image: ImageProps;
  background?: MediaType;
  splitLayoutBackground?: any;
  layout?: string;
  horizontalAlignment?: HorizontalAlignmentEnum;
  verticalAlignment?: VerticalAlignmentEnum;
  toutContent: ToutContentProps;
  isAnimated?: any;
  _testID?: string;
  index?: number;
  threeAcross?: boolean;
  isInGrid?: boolean;
}
enum LayoutEnum {
  Full = 'Full Width',
  Half = 'Half Width',
  Split = 'Split',
}

const componentTypes = {
  [LayoutEnum.Full]: dynamic(() => import('./_FullWidthCard')),
  [LayoutEnum.Half]: dynamic(() => import('./_HalfWidthCard')),
  [LayoutEnum.Split]: dynamic(() => import('./_SplitCard')),
};

const CalloutGridCard = (props: CalloutCardProps) => {
  const {
    backgroundImage,
    background,
    splitLayoutBackground,
    layout,
    toutContent,
    isAnimated,
    _testID,
    index,
    isInGrid = false,
  } = props;
  const isMobile = useIsMobile();

  if (splitLayoutBackground === 'Dark') {
    toutContent.dark = true;
  }
  const { width = 0 } = useWindowSize();

  const hasBGVideo = background?._meta?.schema === VIDEO_META_SCHEMA;
  let vidWidth;

  if (hasBGVideo) {
    vidWidth = width < 600 ? (width * 140 * 16) / 9 : width;
    if (!vidWidth) {
      // Won't have width on server side, so use ssr media query to get placeholder image width
      vidWidth = isMobile ? 600 : width * 0.8;
    }
  }
  const bg = !hasBGVideo ? background || backgroundImage?.image : null;

  const backgroundImageUrl = bg
    ? buildImageSrc(
        bg,
        isMobile ? imageFullWidthXs : imageFullWidthSmAndUp,
        !isMobile && layout === LayoutEnum.Full ? 4 : 2,
        isMobile ? 4 : 2,
      )
    : null;

  const ref = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const yTransition = reduceMotion || isMobile ? 0 : 20;

  useEffect(() => {
    if (isAnimated) {
      const element = ref.current;
      // @ts-expect-error GSAP
      const timeline = new gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start: 'top center',
          end: 'bottom bottom',
          scrub: 0.5,
        },
      });
      timeline.addLabel('entry').from(element, {
        yPercent: yTransition,
        opacity: 0,
      });
    }
  }, []);

  return (
    <div
      className="tw-bg-neutral-700 tw-grid tw-relative"
      ref={ref}
      data-testid="callout-grid-card"
    >
      {hasBGVideo && !!background && vidWidth && (
        <BackgroundVideo
          video={background}
          width={vidWidth}
          fillContainer
          className="tw-absolute tw-inset-0 tw-z-0"
        />
      )}
      <DynamicComponent
        _componentTypes={componentTypes}
        _field="layout"
        {...props}
        backgroundImageUrl={backgroundImageUrl}
        isVideo={hasBGVideo}
        _testID={_testID}
        index={index}
        buttonNegative
        logBackground={splitLayoutBackground === 'Light Gray with Log'}
        splitLayoutBackground={splitLayoutBackground}
        isInGrid={isInGrid}
      />
    </div>
  );
};

export default CalloutGridCard;
